import { pharmacyHelpReturn } from '@/pages/help/helpNavigation.mjs';
import useSupportLanguage from '@/pages/help/useSupportLanguage.jsx';
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import AfriMedAuthMobileRail from "@/components/auth/AfriMedAuthMobileRail.jsx";
import RouteFallback from "@/sot/primitives/RouteFallback.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { readLastAuthedPath } from "@/lib/authedPathCache";
import { readLastRole } from "@/lib/roleCache";
import supabase, { getRedirectUrl } from "@/services/supabaseClient";
import {
  canUseStagingPharmacyOwnerOnboarding,
  resolvePersistedPharmacyAccess,
} from "@/services/pharmacyLaneService.js";

const PENDING_MAGIC_EMAIL_KEY = "afrimed:pending_magic_email";
const PENDING_MAGIC_SENT_AT_KEY = "afrimed:pending_magic_sent_at";
const PENDING_MAGIC_CONTEXT_KEY = "afrimed:pending_magic_context";
const DEFAULT_PHARMACY_NEXT = "/pharmacy/dashboard";
const STAGING_PHARMACY_OWNER_ONBOARDING_NEXT = "/onboarding/pharmacy";

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function withTimeout(promise, ms = 12000, label = "Request") {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out`)), ms),
    ),
  ]);
}

function readPharmacyEntryQuery(search = "") {
  try {
    const params = new URLSearchParams(search || "");
    const blocked = params.get("blocked") === "1";
    const email = normalizeEmail(params.get("email"));
    const error = String(params.get("error") || "").trim().toLowerCase();
    return { blocked, email, error };
  } catch {
    return { blocked: false, email: "", error: "" };
  }
}

function writePendingMagicContext({
  email = "",
  sentAt = Date.now(),
  next = DEFAULT_PHARMACY_NEXT,
  invite = "pharmacy",
} = {}) {
  const em = normalizeEmail(email);
  if (!em) return;

  try {
    localStorage.setItem(PENDING_MAGIC_EMAIL_KEY, em);
    localStorage.setItem(PENDING_MAGIC_SENT_AT_KEY, String(Number(sentAt) || Date.now()));
    localStorage.setItem(
      PENDING_MAGIC_CONTEXT_KEY,
      JSON.stringify({
        email: em,
        firstName: "",
        lastName: "",
        sentAt: Number(sentAt) || Date.now(),
        next: String(next || DEFAULT_PHARMACY_NEXT).trim().startsWith("/")
          ? String(next || DEFAULT_PHARMACY_NEXT).trim()
          : DEFAULT_PHARMACY_NEXT,
        member: "",
        invite: String(invite || "pharmacy").trim().toLowerCase(),
      }),
    );
  } catch {
    // ignore storage failures
  }
}

function resolvePharmacyDisplayName(rows = []) {
  const match = (Array.isArray(rows) ? rows : []).find((row) =>
    String(row?.branch_name || "").trim(),
  );
  return String(match?.branch_name || "").trim();
}

function buildPharmacyRecoveryMessage(errorCode, t) {
  if (errorCode === "callback-timeout" || errorCode === "magic-link-retry") {
    return t(
      "pharmacy.entry.retryNeeded",
      "We couldn’t complete that pharmacy sign-in link. Please request a fresh secure sign-in link below.",
    );
  }
  return "";
}

function PharmacyPasswordField({ value, onChange, disabled = false }) {
  const [visible, setVisible] = React.useState(false);

  return (
    <div className="relative">
      <TextField56
        label="Password"
        type={visible ? "text" : "password"}
        autoComplete="current-password"
        value={value}
        onChange={onChange}
        placeholder="Your pharmacy password"
        tone="surface"
        disabled={disabled}
        className="pr-10"
      />
      <button
        type="button"
        onClick={() => setVisible((next) => !next)}
        aria-label={visible ? "Hide password" : "Show password"}
        className="absolute bottom-3 right-3 flex h-7 w-7 items-center justify-center"
      >
        <img
          src={visible ? "/icons/list-items/visibility.svg" : "/icons/list-items/visibility_off.svg"}
          alt=""
          className="h-5 w-5"
        />
      </button>
    </div>
  );
}

export default function PharmacyEntry() {
  useSupportLanguage();
  const auth = useAuth() || {};
  const location = useLocation();
  const { t } = useLanguage();
  const query = React.useMemo(() => readPharmacyEntryQuery(location.search), [location.search]);

  const helpReturn = pharmacyHelpReturn(new URLSearchParams(location.search).get('next')) || DEFAULT_PHARMACY_NEXT;

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [passwordSubmitting, setPasswordSubmitting] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");
  const [access, setAccess] = React.useState({
    loading: false,
    allowed: false,
    stagingOwnerEligible: false,
    queryStagingOwnerEligible: false,
    pharmacyName: "",
  });

  const activeEmail = normalizeEmail(auth?.user?.email);

  React.useEffect(() => {
    setEmail(query.email || activeEmail || "");
  }, [query.email, activeEmail]);

  React.useEffect(() => {
    const recoveryMessage = buildPharmacyRecoveryMessage(query.error, t);
    setError(recoveryMessage);
  }, [query.error, t]);

  React.useEffect(() => {
    let alive = true;

    async function resolveAccess() {
      if (!auth?.user?.id) {
        const queryStagingOwnerEligible = query.email
          ? await canUseStagingPharmacyOwnerOnboarding(query.email)
          : false;
        if (alive) {
          setAccess({
            loading: false,
            allowed: false,
            stagingOwnerEligible: false,
            queryStagingOwnerEligible,
            pharmacyName: "",
          });
        }
        return;
      }

      setAccess((current) => ({ ...current, loading: true }));
      try {
        const result = await resolvePersistedPharmacyAccess({
          userId: String(auth.user.id || "").trim(),
          email: activeEmail,
        });
        const stagingOwnerEligible =
          !result.allowed && (await canUseStagingPharmacyOwnerOnboarding(activeEmail));
        if (!alive) return;
        setAccess({
          loading: false,
          allowed: !!result.allowed,
          stagingOwnerEligible,
          queryStagingOwnerEligible: false,
          pharmacyName: resolvePharmacyDisplayName(result.rows),
        });
      } catch {
        const stagingOwnerEligible = await canUseStagingPharmacyOwnerOnboarding(activeEmail);
        if (!alive) return;
        setAccess({
          loading: false,
          allowed: false,
          stagingOwnerEligible,
          queryStagingOwnerEligible: false,
          pharmacyName: "",
        });
      }
    }

    resolveAccess();
    return () => {
      alive = false;
    };
  }, [activeEmail, auth?.user?.id, query.email]);

  async function handleSubmit(event) {
    event?.preventDefault?.();
    const targetEmail = normalizeEmail(email);
    if (sending) return;
    if (!targetEmail) {
      setMessage("");
      setError(t("pharmacy.entry.emailRequired", "Enter your email first."));
      return;
    }

    setSending(true);
    setMessage("");
    setError("");

    try {
      const ownerOnboardingEligible = await canUseStagingPharmacyOwnerOnboarding(targetEmail);
      const nextPath = helpReturn !== DEFAULT_PHARMACY_NEXT
        ? helpReturn
        : ownerOnboardingEligible ? STAGING_PHARMACY_OWNER_ONBOARDING_NEXT : DEFAULT_PHARMACY_NEXT;
      const callbackPath = `/auth/callback?next=${encodeURIComponent(nextPath)}`;
      const callbackUrl = getRedirectUrl(callbackPath);
      const runtimeCallbackUrl =
        typeof window !== "undefined"
          ? `${window.location.origin}${callbackPath}`
          : callbackUrl;

      const sendOtp = async (redirectTo) =>
        withTimeout(
          supabase.auth.signInWithOtp({
            email: targetEmail,
            options: {
              emailRedirectTo: redirectTo,
              shouldCreateUser: true,
              data: {
                role: "pharmacy",
                primary_role: "pharmacy",
              },
            },
          }),
          12000,
          "Send secure sign-in link",
        );

      let { error: sendError } = await sendOtp(runtimeCallbackUrl);
      if (sendError) {
        const message = String(sendError?.message || "").toLowerCase();
        const likelyRedirectIssue =
          message.includes("confirmation email") ||
          message.includes("redirect") ||
          message.includes("callback");

        if (likelyRedirectIssue) {
          const retry = await sendOtp(callbackUrl);
          sendError = retry.error;
        }
      }

      if (sendError) {
        const lower = String(sendError?.message || "").toLowerCase();
        if (lower.includes("failed to fetch") || lower.includes("network")) {
          throw new Error(
            t(
              "pharmacy.entry.networkIssue",
              "Network issue while sending the pharmacy sign-in link. Please try again.",
            ),
          );
        }
        if (lower.includes("confirmation email")) {
          throw new Error(
            t(
              "pharmacy.entry.emailUnavailable",
              "Pharmacy email sign-in is temporarily unavailable. Please try again shortly.",
            ),
          );
        }
        throw sendError;
      }

      writePendingMagicContext({
        email: targetEmail,
        sentAt: Date.now(),
        next: nextPath,
      });
      setMessage(
        t(
          "pharmacy.entry.linkSent",
          "We sent your secure sign-in link. Open the newest email on this same browser or device to continue to Pharmacy Dashboard.",
        ),
      );
    } catch (err) {
      setError(
        String(
          err?.message ||
            t(
              "pharmacy.entry.sendError",
              "We couldn’t send the pharmacy sign-in link. Please try again.",
            ),
        ),
      );
    } finally {
      setSending(false);
    }
  }

  async function handlePasswordSubmit(event) {
    event?.preventDefault?.();
    const targetEmail = normalizeEmail(email);
    const targetPassword = String(password || "");
    if (!targetEmail || !targetPassword || passwordSubmitting) return;

    setPasswordSubmitting(true);
    setMessage("");
    setError("");

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: targetEmail,
        password: targetPassword,
      });
      if (signInError) throw signInError;

      const signedInUserId = String(data?.user?.id || "").trim();
      const accessResult = signedInUserId
        ? await resolvePersistedPharmacyAccess({
            userId: signedInUserId,
            email: targetEmail,
          }).catch(() => ({ allowed: false }))
        : { allowed: false };
      const ownerOnboardingEligible = !accessResult?.allowed
        ? await canUseStagingPharmacyOwnerOnboarding(targetEmail).catch(() => false)
        : false;

      setPassword("");
      if (accessResult?.allowed) {
        window.location.assign(helpReturn);
        return;
      }
      if (ownerOnboardingEligible) {
        window.location.assign(STAGING_PHARMACY_OWNER_ONBOARDING_NEXT);
        return;
      }

      setError(
        t(
          "pharmacy.entry.passwordAccessDenied",
          "We could not open Pharmacy Dashboard for those details. Use a linked pharmacy account or request a secure link.",
        ),
      );
    } catch {
      setError(
        t(
          "pharmacy.entry.passwordError",
          "We could not sign you in with those details. Check your email and password, then try again.",
        ),
      );
    } finally {
      setPasswordSubmitting(false);
    }
  }

  const locallyKnownIncompletePharmacyOnboarding = (() => {
    const userId = String(auth?.user?.id || "").trim();
    if (!userId || auth?.profile?.onboarding_completed === true) return false;
    const lastRole = readLastRole(userId);
    const lastPath = readLastAuthedPath(userId);
    return lastRole === "pharmacy" && lastPath.startsWith(STAGING_PHARMACY_OWNER_ONBOARDING_NEXT);
  })();

  if (locallyKnownIncompletePharmacyOnboarding) {
    return <Navigate to={STAGING_PHARMACY_OWNER_ONBOARDING_NEXT} replace />;
  }

  const shouldShowLoading = auth.loading || (!!auth?.user?.id && access.loading);
  if (shouldShowLoading) {
    return <RouteFallback label={t("pharmacy.entry.loading", "Opening Pharmacy...")} />;
  }

  if (auth?.user?.id && access.allowed) {
    return <Navigate to={helpReturn} replace />;
  }

  if (auth?.user?.id && access.stagingOwnerEligible) {
    return <Navigate to={STAGING_PHARMACY_OWNER_ONBOARDING_NEXT} replace />;
  }

  const pharmacyDisplayName =
    String(access.pharmacyName || "").trim() ||
    t("pharmacy.entry.defaultPharmacyName", "AfriMed™ Pharmacy");
  const currentInputEmail = normalizeEmail(email);
  const blockedContextEmail = query.email || activeEmail;
  const blockedFromQuery =
    query.blocked &&
    !access.queryStagingOwnerEligible &&
    (!blockedContextEmail || currentInputEmail === normalizeEmail(blockedContextEmail));
  const blockedFromSignedInEmail =
    !!auth?.user?.id &&
    !access.allowed &&
    (!activeEmail || currentInputEmail === activeEmail);
  const blocked = blockedFromQuery || blockedFromSignedInEmail;
  const staleBlockedStagingOwnerInvite =
    query.blocked && access.queryStagingOwnerEligible && !auth?.user?.id;
  const supportPoints = [
    t(
      "pharmacy.entry.point1",
      "Same AfriMed™ magic-link sign-in, but through a pharmacy-first door.",
    ),
    t(
      "pharmacy.entry.point2",
      "Linked pharmacy owners and branch members land directly in the pharmacy workspace, whether the linked address is work or personal.",
    ),
    t(
      "pharmacy.entry.point3",
      "Emails not linked to a pharmacy branch stay in this lane instead of leaking into doctor or patient home.",
    ),
  ];
  const supportContent = (
    <div className="w-full">
      <img
        src="/assets/images/afrimed_logo.png"
        alt="AfriMed Pharmacy"
        className="hidden h-auto w-[154px] max-w-full object-contain lg:block"
        draggable="false"
      />

      <div className="mt-6 hidden space-y-2 lg:block">
        <p className="text-title-large text-on-surface">
          {t("pharmacy.entry.title", "Pharmacy sign in")}
        </p>
        <p className="text-body-large text-on-surface-var">
          {t(
            "pharmacy.entry.subtitle",
            "Use the email linked to your pharmacy owner or branch-member record to receive a secure sign-in link for Pharmacy Dashboard.",
          )}
        </p>
      </div>

      <div className="mt-10 space-y-5">
        {supportPoints.map((copy) => (
          <div key={copy} className="flex items-start gap-3">
            <div className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-[rgb(var(--afrimed-primary))]" />
            <p className="text-body-medium text-on-surface-var">{copy}</p>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <AfriMedAuthMobileRail
      title={t("pharmacy.entry.cardTitle", "Welcome to {{pharmacyName}}").replace(
        "{{pharmacyName}}",
        pharmacyDisplayName,
      )}
      subtitle={t(
        "pharmacy.entry.cardSubtitle",
        "Enter the email linked to your pharmacy branch record and AfriMed™ will send a secure sign-in link.",
      )}
      logoAlt="AfriMed Pharmacy"
      desktopAside={supportContent}
      outerClassName="auth-bg min-h-[100dvh] w-full overflow-x-hidden px-4"
      layoutClassName="min-h-[100dvh]"
      contentClassName="w-full max-w-[375px] text-center lg:max-w-[520px]"
      logoClassName="mx-auto mb-12 h-auto w-[135px]"
      headerClassName="space-y-3 text-center"
      subtitleClassName="text-body-large text-on-surface-var"
    >

              {blocked ? (
                <div className="mt-6 rounded-[12px] border border-[rgba(184,74,58,0.28)] bg-[rgba(184,74,58,0.10)] px-4 py-3 text-body-medium text-on-surface">
                  <div className="font-medium text-[rgb(var(--clr-on-surface))]">
                    {t(
                      "pharmacy.entry.notLinkedTitle",
                      "This email is not linked to a pharmacy branch yet.",
                    )}
                  </div>
                  <div className="mt-1 text-body-medium text-on-surface-var">
                    {t(
                      "pharmacy.entry.notLinkedBody",
                      "Use a linked pharmacy branch email to continue to Pharmacy Dashboard.",
                    )}
                  </div>
                </div>
              ) : null}

              {staleBlockedStagingOwnerInvite ? (
                <div className="mt-6 rounded-[12px] border border-[rgba(var(--afrimed-primary),0.2)] bg-[rgba(var(--afrimed-primary),0.08)] px-4 py-3 text-body-medium text-on-surface">
                  <div className="font-medium text-[rgb(var(--clr-on-surface))]">
                    {t(
                      "pharmacy.entry.stagingOwnerReadyTitle",
                      "This pharmacy staging invite is ready.",
                    )}
                  </div>
                  <div className="mt-1 text-body-medium text-on-surface-var">
                    {t(
                      "pharmacy.entry.stagingOwnerReadyBody",
                      "Send a fresh secure sign-in link with this email to continue to pharmacy onboarding.",
                    )}
                  </div>
                </div>
              ) : null}

              {error ? (
                <div className="mt-6 rounded-[12px] border border-[rgba(184,74,58,0.28)] bg-[rgba(184,74,58,0.10)] px-4 py-3 text-body-medium text-on-surface">
                  {error}
                </div>
              ) : null}

              {message ? (
                <div className="mt-6 rounded-[12px] border border-[rgba(var(--afrimed-primary),0.2)] bg-[rgba(var(--afrimed-primary),0.08)] px-4 py-3 text-body-medium text-on-surface">
                  {message}
                </div>
              ) : null}

              <form className="mt-8 space-y-5" onSubmit={handlePasswordSubmit}>
                <TextField56
                  label={t("pharmacy.entry.emailLabel", "Email")}
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  value={email}
                  onChange={(nextEmail) => {
                    setEmail(nextEmail);
                    if (error) setError("");
                    if (message) setMessage("");
                  }}
                  placeholder={t("pharmacy.entry.emailPlaceholder", "name@example.com")}
                  tone="surface"
                />

                <PharmacyPasswordField
                  value={password}
                  onChange={setPassword}
                  disabled={passwordSubmitting || sending}
                />

                <button
                  type="submit"
                  disabled={passwordSubmitting || !normalizeEmail(email) || !String(password || "").trim()}
                  className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-[12px] bg-[rgb(var(--afrimed-primary))] px-5 text-title-medium font-medium tracking-[-0.015em] text-[rgb(var(--afrimed-on-primary))] transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-55"
                >
                  <span>
                    {passwordSubmitting
                      ? t("pharmacy.entry.signingIn", "Signing in...")
                      : t("pharmacy.entry.passwordSignIn", "Sign in")}
                  </span>
                  <ChevronRight className="h-5 w-5" />
                </button>
              </form>

              <div className="mt-7 space-y-2 text-center">
                <h2 className="text-title-medium text-on-surface">
                  {t("pharmacy.entry.firstTimeTitle", "First-time setup or forgot password?")}
                </h2>
                <p className="text-body-medium text-on-surface-var">
                  {t(
                    "pharmacy.entry.firstTimeCopy",
                    "Use a secure link to access your pharmacy account, then set or change your password from My account.",
                  )}
                </p>
              </div>

              <form className="mt-4 space-y-3 text-center" onSubmit={handleSubmit}>
                <button
                  type="submit"
                  disabled={sending}
                  className="mx-auto inline-flex min-h-10 items-center justify-center gap-2 rounded-full bg-[var(--state-primary-12)] px-5 text-label-large font-medium text-[rgb(var(--afrimed-primary))] transition hover:bg-[var(--state-primary-16)] disabled:cursor-not-allowed disabled:opacity-55"
                >
                  <span>
                    {sending
                      ? t("pharmacy.entry.sending", "Sending secure link...")
                      : t("pharmacy.entry.send", "Email me a secure link")}
                  </span>
                </button>
                <p className="text-body-small text-on-surface-var">
                  {t("pharmacy.entry.passwordNote", "Password sign-in is used for normal pharmacy access.")}
                </p>
              </form>

              <p className="mt-6 text-center text-body-small text-on-surface-var">
                {t(
                  "pharmacy.entry.needAccess",
                  "Need access? Contact the AfriMed™ platform administrator to provision your pharmacy account before sign in.",
                )}
              </p>
    </AfriMedAuthMobileRail>
  );
}
